/**
 * Widgets Tab Component
 * Manages partner payment widgets
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Code, Plus, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { generateWidget } from '../../lib/api';
import { toast } from 'sonner';

const WidgetsTab = ({ widgets, onWidgetCreated }) => {
  const [showCreateWidget, setShowCreateWidget] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatedWidget, setGeneratedWidget] = useState(null);
  const [widgetForm, setWidgetForm] = useState({
    widget_type: 'payment_button',
    amount: '',
    currency: 'USD',
    description: ''
  });

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleGenerateWidget = async () => {
    setSubmitting(true);
    try {
      const config = {
        ...widgetForm,
        amount: widgetForm.amount ? parseFloat(widgetForm.amount) : null
      };
      const res = await generateWidget(config);
      setGeneratedWidget(res.data);
      toast.success('Widget generated!');
      onWidgetCreated?.();
    } catch (error) {
      toast.error('Failed to generate widget');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 mt-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading font-semibold">Payment Widgets</h2>
        <Dialog open={showCreateWidget} onOpenChange={setShowCreateWidget}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" data-testid="create-widget-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Widget
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Generate Widget</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Widget Type</Label>
                <Select value={widgetForm.widget_type} onValueChange={(v) => setWidgetForm({ ...widgetForm, widget_type: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment_button">Payment Button</SelectItem>
                    <SelectItem value="checkout">Checkout Form</SelectItem>
                    <SelectItem value="donation">Donation Widget</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (optional)</Label>
                  <Input
                    type="number"
                    value={widgetForm.amount}
                    onChange={(e) => setWidgetForm({ ...widgetForm, amount: e.target.value })}
                    placeholder="Leave empty for custom"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={widgetForm.currency} onValueChange={(v) => setWidgetForm({ ...widgetForm, currency: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={widgetForm.description}
                  onChange={(e) => setWidgetForm({ ...widgetForm, description: e.target.value })}
                  placeholder="Payment for..."
                  className="rounded-xl"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleGenerateWidget} disabled={submitting} className="w-full rounded-xl">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Widget'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {generatedWidget && (
        <Card className="border-emerald-500">
          <CardHeader>
            <CardTitle className="text-lg">Generated Widget Code</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Embed Code</Label>
              <pre className="bg-muted p-3 rounded-xl text-xs overflow-x-auto mt-2">
                {generatedWidget.embed_code}
              </pre>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => copyToClipboard(generatedWidget.embed_code, 'Embed code')}>
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <div>
              <Label>Direct Link</Label>
              <div className="flex gap-2 mt-2">
                <Input value={generatedWidget.direct_link} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(generatedWidget.direct_link, 'Link')}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {widgets.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Code className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No widgets created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {widgets.map((widget) => (
            <Card key={widget.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium capitalize">{widget.config?.widget_type?.replace('_', ' ')}</p>
                    <p className="text-sm text-muted-foreground font-mono">{widget.id.slice(0, 8)}...</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <a href={`https://parachpay.com/pay/partner/${widget.id}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default WidgetsTab;
